import {
    getFolderAccessUnsupportedMessage,
    hasDirectoryPicker,
    normaliseDirectoryPickerError,
} from './folder-access-support';
import { matchProjectSubfolder, INTAKE_ROLES } from '../config/folder-conventions.js';
import { SYNC_MANIFEST_SCHEMA_VERSION } from './sync-folder-manifest';

const INTAKE_ROLE_SET_FOR_PROJECT_DETECTION = new Set(INTAKE_ROLES);

/**
 * Detect whether a folder's immediate subfolders include the Customer-Zero
 * INTAKE roles (`01. RFQ Package` and/or `02. Site Documentation Package`).
 *
 * v5.19.59 (Peter 2026-05-12): the parent project folder in Customer-Zero
 * deployments has 10 numbered subfolders with ZERO direct files at the
 * root. shouldTreatSelectedFolderAsProject's evidence-ratio heuristic
 * (2/10 = 0.2 < 0.6) returned false, so the walker descended into the
 * subfolders and treated EACH subfolder as a separate project. This
 * meant operator selection state was scoped to a single subfolder; you
 * couldn't pick files from 01. RFQ Package AND 02. Site Documentation
 * Package in the same Import action. Recognising the CZ structure here
 * collapses both subfolders under one project entry as intended.
 *
 * @param {Array<[string, FileSystemHandle]>} entries
 * @returns {boolean}
 */
function hasCustomerZeroIntakeSubfolder(entries: any[] = []) {
    for (const [name, handle] of entries) {
        if (!handle || handle.kind !== 'directory') continue;
        const spec = matchProjectSubfolder(name);
        if (spec && INTAKE_ROLE_SET_FOR_PROJECT_DETECTION.has(spec.role)) return true;
    }
    return false;
}

// FE-CP-3 (v5.19.56): decide whether a file should default-select based on
// the Customer-Zero subfolder it lives in. Pull the first path segment of
// `relativePath` and match against the CZ catalogue:
//   - in an intake subfolder (01. RFQ Package / 02. Site Documentation Package)
//     → default-select (when otherwise eligible).
//   - in the output (03. Quote) or any other numbered subfolder (04+)
//     → default-DESELECT regardless of readability.
//   - not in any CZ subfolder (root-level file, legacy `photos/`/`documents/`,
//     unknown subfolder) → fall through to the legacy `baseSelected` rule.
const INTAKE_ROLE_SET = new Set(INTAKE_ROLES);
function defaultSelectionForRelativePath(relativePath: any, baseSelected: any) {
    const slashIdx = String(relativePath || '').indexOf('/');
    if (slashIdx <= 0) return baseSelected; // root-level file
    const firstSegment = relativePath.slice(0, slashIdx);
    const spec = matchProjectSubfolder(firstSegment);
    if (!spec) return baseSelected; // unknown subfolder — legacy behaviour
    if (INTAKE_ROLE_SET.has(spec.role)) return baseSelected;
    return false; // output / 04+ subfolders are out-of-scope by default
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'jfif', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'tif', 'tiff']);
// Exported for the upload parity test: every one of these must resolve to a
// content type the server's project_document lane registers.
export const DOCUMENT_EXTENSIONS: ReadonlySet<string> = new Set(['pdf', 'doc', 'docx']);
export const EMAIL_EXTENSIONS: ReadonlySet<string> = new Set(['eml', 'msg']);
export const TEXT_EXTENSIONS: ReadonlySet<string> = new Set(['txt', 'md', 'text']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'webm', 'mkv']);
// The recordings a Stage 1 drop transcribes into Inspector Notes (categorizeFile).
export const AUDIO_EXTENSIONS: ReadonlySet<string> = new Set(['mp3', 'wav', 'm4a', 'ogg', 'flac', 'aac', 'wma']);

const QUOTEMAKER_OUTPUT_FILES = new Set([
    'quotedata.json',
    'syncmanifest.json',
    'project_index.md',
    'ai_learnings.json',
    'risk_report.txt',
]);

const IGNORED_DIRECTORIES = new Set([
    '.git',
    '.svn',
    '.hg',
    'node_modules',
    'dist',
    'build',
    'learnings',
]);

const EVIDENCE_DIRECTORY_PATTERNS = [
    /\brfq\b/i,
    /\bquote[-_\s]?request\b/i,
    /\brequest(s)?\b/i,
    /\bphoto(s)?\b/i,
    /\bimage(s)?\b/i,
    /\bvideo(s)?\b/i,
    /\bemail(s)?\b/i,
    /\bmsg\b/i,
    /\bdoc(s|uments)?\b/i,
    /\bpdf(s)?\b/i,
    /\bplan(s)?\b/i,
    /\bdrawing(s)?\b/i,
    /\breport(s)?\b/i,
    /\bnote(s)?\b/i,
    /\bscope\b/i,
    /\binspection\b/i,
];

const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const DEFAULT_DISCOVERY_DEPTH = 5;
const DEFAULT_DISCOVERY_DIRECTORY_LIMIT = 800;

// FE-SUBFOLDER-1 (2026-06-13): canonical per-project file-collection depth.
// Raised from the historic 5 to 8 so files nested up to 8 folders under the
// project root are still collected instead of silently dropped. The drop-walk
// walker (src/utils/drop-walk.ts) shares this same value as its canonical
// depth policy so the SAME folder yields the SAME files regardless of whether
// it was loaded via the folder picker or a drag-drop. When the cap IS hit,
// scanFilesInDirectory pushes an operator-visible warning into warnings[]
// (surfaced by FileExplorer.jsx via selectedProject.scanWarnings → InlineWarning)
// rather than skipping silently. If you change this, change DROP_WALK_MAX_DEPTH
// in src/utils/drop-walk.ts in the same commit.
const DEFAULT_SCAN_DEPTH = 8;

function normaliseExplorerPath(path = '') {
    return String(path || '')
        .replace(/\\/g, '/')
        .split('/')
        .map(part => part.trim())
        .filter(Boolean)
        .join('/');
}

function joinExplorerPath(basePath = '', name = '') {
    const base = normaliseExplorerPath(basePath);
    const leaf = String(name || '').trim();
    if (!base) return leaf;
    if (!leaf) return base;
    return `${base}/${leaf}`;
}

function projectFolderRoleForDepth(depth: any) {
    return depth <= 1 ? 'child-project-folder' : 'nested-project-folder';
}

// Map raw FileSystemAccess / DOMException errors into actionable
// human-readable reasons for the scan-warning manifest. Three common
// real-world causes for "we couldn't read this file":
//   • OneDrive / iCloud / Dropbox cloud-only stub — file shows in Explorer
//     but the bytes haven't been downloaded. Browser FSA throws
//     NotFoundError or NotReadableError mentioning "system error" / "OS".
//   • File locked by another process (Outlook holds .msg files open while
//     they're displayed; Word holds .doc/.docx). Throws NotReadableError
//     or InvalidStateError mentioning "use" / "shar" / "lock".
//   • Permission revoked / handle stale — NotAllowedError / SecurityError.
function errorMessage(err: any, hintName = '') {
    const raw = err?.message || err?.name || '';
    const text = raw.toLowerCase();
    const ext = extname(hintName);
    const isEmail = EMAIL_EXTENSIONS.has(ext);
    if (/cloud|sync|hydrat|onedrive|icloud|dropbox|placeholder/.test(text)) {
        return 'Cloud-only file - open the folder in File Explorer and right-click → Always keep on this device.';
    }
    if (/use|shar|lock|in use|denied|busy/.test(text)) {
        return isEmail
            ? 'File is open in Outlook - close the message and rescan.'
            : 'File is open in another program - close it and rescan.';
    }
    if (/notallowed|permission|security/.test(text)) {
        return 'Permission denied - re-grant folder access in the address bar.';
    }
    if (/notfound|enoent|no such/.test(text)) {
        return 'File no longer exists at this path.';
    }
    if (/notreadable|operation/.test(text) && isEmail) {
        // Most generic NotReadableError on .msg files traces to either
        // a OneDrive stub or an Outlook lock — surface both possibilities
        // so the user knows where to look.
        return 'Couldn\'t read the email - likely a cloud-only file or open in Outlook.';
    }
    return raw || 'Folder entry could not be read.';
}

// 2026-05-04 hot-fix: removed `.split('?')[0].split('#')[0]` URL-fragment
// stripping. The function operates on FILENAMES, not URLs — `#` and `?` are
// legal characters in filenames. The strip silently truncated names like
// "FW_ Quote Request - S_Plan 54295 - Job #93100.msg" to before the `#`,
// which removed the `.msg` extension entirely → file classified as
// 'unsupported' → size never read (defaulted to 0 KB) → Peter saw .msg
// emails as 0 KB unsupported in production v5.14.98.
function extname(name = '') {
    const clean = String(name || '');
    const dot = clean.lastIndexOf('.');
    return dot >= 0 ? clean.slice(dot + 1).toLowerCase() : '';
}

export function classifyExplorerFile(fileOrName: any) {
    const name = typeof fileOrName === 'string' ? fileOrName : fileOrName?.name;
    const ext = extname(name);
    if (IMAGE_EXTENSIONS.has(ext)) return 'image';
    if (DOCUMENT_EXTENSIONS.has(ext)) return 'document';
    if (EMAIL_EXTENSIONS.has(ext)) return 'email';
    if (TEXT_EXTENSIONS.has(ext)) return 'text';
    if (VIDEO_EXTENSIONS.has(ext)) return 'video';
    if (AUDIO_EXTENSIONS.has(ext)) return 'audio';
    return 'unsupported';
}

export function isSupportedExplorerFile(name: any) {
    return classifyExplorerFile(name) !== 'unsupported';
}

export function isGeneratedQuoteMakerFile(name: any) {
    return QUOTEMAKER_OUTPUT_FILES.has(String(name || '').toLowerCase());
}

export function looksLikeEvidenceDirectory(name: any) {
    const text = String(name || '').trim();
    if (!text) return false;
    return EVIDENCE_DIRECTORY_PATTERNS.some(pattern => pattern.test(text));
}

function directEntrySummary(entries: any[] = []) {
    const directories: any[] = [];
    const files: any[] = [];
    for (const [name, handle] of entries) {
        if (!handle) continue;
        const lower = String(name || '').toLowerCase();
        if (handle.kind === 'directory') {
            if (!IGNORED_DIRECTORIES.has(lower)) directories.push({ name, handle });
            continue;
        }
        if (handle.kind === 'file') files.push({ name, handle });
    }
    return { directories, files };
}

function selectedFolderProjectSignals(entries: any[] = []) {
    const { directories, files } = directEntrySummary(entries);
    const hasSyncManifest = files.some(({ name }) => String(name || '').toLowerCase() === 'syncmanifest.json');
    const hasDirectSupportedFile = files.some(({ name }) => (
        !isGeneratedQuoteMakerFile(name) && isSupportedExplorerFile(name)
    ));
    const evidenceDirectories = directories
        .filter(({ name }) => looksLikeEvidenceDirectory(name))
        .map(({ name }) => name);

    return {
        hasSyncManifest,
        hasDirectSupportedFile,
        evidenceDirectories,
        directoryCount: directories.length,
    };
}

export function shouldTreatSelectedFolderAsProject(entries: any[] = [], options: any = {}) {
    if (options.forceSelectedFolderAsProject === true) return true;
    if (options.allowSelectedFolderAsProject === false) return false;

    const signals = selectedFolderProjectSignals(entries);
    if (signals.hasSyncManifest) return true;
    // v5.19.59 (Peter 2026-05-12): Customer-Zero project folder layout
    // has 10 numbered subfolders (01. RFQ Package … 10. Defects) with
    // ZERO direct files at the project root. The evidence-ratio heuristic
    // below returns 2/10 = 0.2 < 0.6 → previously fell through to "not a
    // project" and the walker descended into each subfolder, treating
    // them as separate projects. Recognising the CZ intake subfolders
    // here keeps the parent folder as the single project entry so the
    // operator can select files from 01 AND 02 in one Import action.
    if (hasCustomerZeroIntakeSubfolder(entries)) return true;
    if (signals.hasDirectSupportedFile && signals.directoryCount === 0) return true;
    if (signals.hasDirectSupportedFile && signals.evidenceDirectories.length > 0) return true;
    if (signals.evidenceDirectories.length === 0) return false;

    const evidenceRatio = signals.directoryCount > 0
        ? signals.evidenceDirectories.length / signals.directoryCount
        : 0;
    return signals.directoryCount <= 4 || evidenceRatio >= 0.6;
}

function shouldTreatDiscoveredFolderAsProject(entries: any[] = [], options: any = {}) {
    const signals = selectedFolderProjectSignals(entries);
    if (signals.hasSyncManifest) return true;
    // v5.19.59: also recognise CZ layout in the discovered-folder path
    // (the walker descending into the sync root → each project folder).
    if (hasCustomerZeroIntakeSubfolder(entries)) return true;
    // Once we are below the chosen root, a direct supported RFQ/email/photo
    // is strong evidence that this folder is a job folder, even if it also
    // contains miscellaneous child folders. The selected root keeps the
    // stricter rule above so a parent folder with a master RFQ plus sibling
    // jobs does not collapse into one project.
    if (signals.hasDirectSupportedFile) return true;
    return shouldTreatSelectedFolderAsProject(entries, options);
}

// ─── FE-SUBFOLDER-1 smart project grouping (2026-06-13) ─────────────────────
//
// PROBLEM: when a real job folder has many subfolders and a low evidence-dir
// ratio (e.g. Job/{Photos,Plans,Reports,Old,Misc,Archive,Temp,Backup}/),
// shouldTreatSelectedFolderAsProject returns FALSE (ratio 3/8 < 0.6, >4 dirs),
// so the legacy walk DESCENDS into the folder and promotes EACH child holding
// any supported file to its OWN phantom project ("Photos", "Plans", …). One
// real job fragments into many sidebar/queue entries.
//
// FIX: a NEW async pass that, when the sync rule would fragment, peeks one
// level into each child subfolder and decides whether the parent is actually
// ONE job whose children are evidence buckets — vs a true PARENT folder
// containing SIBLING jobs (which must STILL split).
//
// The discriminator (orchestrator-specified):
//   • sibling job   → child has its own SyncManifest, a CZ intake subfolder,
//                     its own RFQ/quote-request-class brief document/email at
//                     its top level, or its own evidence subdirectories
//                     (a job folder organised into Photos/Plans/…).
//   • evidence bucket → an evidence-named directory (Photos/Reports/Plans/…),
//                     OR a non-evidence child that holds files directly but has
//                     NO subdirectories and NO RFQ-class brief (a content/overflow
//                     leaf like Old/ or Misc/).
//   • structural    → a non-evidence child with subdirectories but no brief
//                     (e.g. PICA Group/2026/…). These contain jobs DEEPER, so
//                     they BLOCK the merge and we keep descending.
//
// MERGE rule: treat the parent as ONE project iff it has ≥1 evidence bucket,
// ZERO sibling jobs, and ZERO structural children. Any sibling job OR any
// structural child blocks the merge so true parents-of-jobs still fragment
// into the correct separate projects.
//
// Gated behind `qm_disable_smart_project_grouping` (default OFF = grouping ON).

function isSmartProjectGroupingDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            // Read through window.localStorage (not the bare global) so the
            // guard and the read agree — they're the same object in a real
            // browser, and this form is unit-testable via a stubbed window.
            return window.localStorage.getItem('qm_disable_smart_project_grouping') === 'true';
        }
    } catch { /* localStorage unavailable (SSR / Node tests) */ }
    return false;
}

// Brief-class direct file: a document (pdf/doc/docx) or email (msg/eml) at a
// child's top level. A direct brief at a child's root is the "this child is its
// OWN job" signal — even a minimal job folder is just one PDF in a folder.
// We deliberately bias toward NOT merging when a child carries its own brief:
// the orchestrator's stated priority is "CAREFUL not to over-merge", and a
// false split (two real-job folders kept separate) is far cheaper than a false
// merge (two jobs Frankensteined into one). Photos / videos / loose notes are
// NOT briefs — those are evidence content. Evidence-NAMED directories
// (Plans/Reports/…) are short-circuited to buckets BEFORE this test in
// classifyChildForGrouping, so plan/report PDFs inside them don't read as a
// standalone brief.
function hasBriefDirectFile(files: any[] = []) {
    return files.some(({ name }) => {
        if (isGeneratedQuoteMakerFile(name)) return false;
        const kind = classifyExplorerFile(name);
        return kind === 'document' || kind === 'email';
    });
}

/**
 * Classify a CHILD subfolder of a would-fragment parent as one of:
 *   'sibling-job'     — its own job; parent must NOT merge.
 *   'evidence-bucket' — content of THIS job (Photos/Plans/…/overflow leaf).
 *   'structural'      — navigational folder containing jobs deeper; block merge.
 *
 * Async because it must enumerate the child's immediate entries. Read-only.
 *
 * @param {string} childName
 * @param {FileSystemHandle} childHandle
 * @returns {Promise<'sibling-job' | 'evidence-bucket' | 'structural'>}
 */
async function classifyChildForGrouping(childName: any, childHandle: any) {
    // Evidence-named directories are ALWAYS evidence buckets, regardless of
    // their contents (a "Plans" folder full of plan PDFs is evidence, not a
    // sibling job). Short-circuit before the brief test so plan/report docs
    // inside an evidence-named folder don't read as a brief.
    //
    // KNOWN EDGE (recorded debt — v5.41.69 FE-SUBFOLDER-1): this name-only
    // short-circuit can MIS-READ an address that merely CONTAINS an evidence
    // word — e.g. a job folder literally named "5 Plans Ave" or "9 Photos Rd"
    // (street names) — as an evidence bucket, even though it holds its own
    // brief. If a would-fragment parent's children are ALL such names, the
    // parent over-merges into one project. It needs every sibling to coincide
    // (one neutral-named sibling defeats it), only fires on the fragmentation
    // path, and is fully reversible via `qm_disable_smart_project_grouping`,
    // so it ships as known debt. The clean fix is an ADDRESS discriminator:
    // skip this short-circuit when `childName` looks like a street address
    // (leading street-number + a street-type token like Ave/Rd/St/…) so a
    // pure evidence label ("Plans") still buckets while an address ("5 Plans
    // Ave") falls through to the brief/manifest/own-subdir tests below and is
    // correctly classified as a sibling job. Deliberately NOT done here: the
    // naive "also run hasBriefDirectFile for evidence-named children" would
    // RE-BREAK the common case (a real "Plans" folder of plan PDFs would read
    // as a brief and false-split). Do it as an address-aware fast-follow with
    // its own tests, not a one-liner.
    if (looksLikeEvidenceDirectory(childName)) return 'evidence-bucket';

    let childEntries: any[] = [];
    try {
        childEntries = await listEntries(childHandle);
    } catch {
        // Unreadable child — treat as a benign empty bucket so a transient
        // permission error on one child doesn't block grouping the parent.
        return 'evidence-bucket';
    }

    const { directories, files } = directEntrySummary(childEntries);
    const signals = selectedFolderProjectSignals(childEntries);

    // Strong sibling-job markers.
    if (signals.hasSyncManifest) return 'sibling-job';
    if (hasCustomerZeroIntakeSubfolder(childEntries)) return 'sibling-job';
    // A direct document/email at the child's root → it carries its own brief →
    // treat as a standalone job (even a minimal one-PDF job). Conservative: a
    // false split beats a false merge.
    if (hasBriefDirectFile(files)) return 'sibling-job';
    // A child organised into its own evidence subfolders (Photos/Plans/…) is a
    // job folder in its own right, not a single evidence bucket of the parent.
    if (signals.evidenceDirectories.length > 0) return 'sibling-job';

    // No job markers. A child with subdirectories (but no brief / evidence
    // structure) is a navigational/structural folder that may hide jobs
    // deeper — block the merge so the walker descends into it.
    if (directories.length > 0) return 'structural';

    // Leaf child: media/text files only (or empty), no subdirs, no brief →
    // content/evidence bucket of THIS job.
    return 'evidence-bucket';
}

/**
 * Decide whether a folder the SYNC rules would fragment is actually one
 * project (its children are evidence buckets, none are sibling jobs, none are
 * structural). Returns true only when grouping is enabled AND the merge is
 * unambiguous.
 *
 * @param {Array<[string, FileSystemHandle]>} entries — parent's immediate entries
 * @returns {Promise<boolean>}
 */
async function shouldGroupFolderAsSingleProject(entries: any[] = []) {
    if (isSmartProjectGroupingDisabled()) return false;

    const { directories } = directEntrySummary(entries);
    if (directories.length === 0) return false; // nothing to fragment

    let evidenceBuckets = 0;
    for (const { name, handle } of directories) {
        const verdict = await classifyChildForGrouping(name, handle);
        if (verdict === 'sibling-job' || verdict === 'structural') return false;
        if (verdict === 'evidence-bucket') evidenceBuckets += 1;
    }
    // Require at least one affirmative evidence bucket so we don't merge a
    // folder of nothing-but-empty dirs into a phantom project.
    return evidenceBuckets > 0;
}

// ─── Sibling-job census (2026-08-19) ────────────────────────────────────────
//
// PROBLEM (Peter, live UAT on the staging wave): a parent folder holding
// MULTIPLE job folders was swallowed into ONE project because its ROOT
// carried a stray SyncManifest.json (the folder had once been a synced quote
// folder; other completed-quote folders were later copied INTO it).
// shouldTreatSelectedFolderAsProject's manifest rule fires before any child
// is examined, so every child job collapsed into "reference subfolders" of
// the manifest's identity — a Frankenstein bundling two unrelated jobs whose
// checklist then happily reported "All reference files in".
//
// FIX: before accepting a selected/discovered folder as ONE project, count
// children that are UNAMBIGUOUS sibling jobs. Two or more outrank EVERY
// single-project rule, including the root manifest — the folder is a parent
// of projects and the walk descends. Markers, conservative by design:
//   • strong — the child carries its own SyncManifest.json, or its own CZ
//     intake subfolder (01. RFQ Package / 02. Site Documentation Package):
//     that child IS a QuoteMaker project or a convention job folder.
//   • medium — a non-evidence-named child holding its own brief document
//     (pdf/doc/email) AND its own media or subfolder structure — a
//     self-contained job copy. A docs-only leaf ("Old quotes",
//     "Correspondence") deliberately does NOT count, so a real job's
//     archive subfolders cannot split it.
// A canonical CZ pack name (01. RFQ Package … 10. Defects) never counts:
// "03. Quote" holds the produced PDF and would otherwise read as a brief.
//
// Kill switch: qm_disable_sibling_job_census (default OFF = census ON).

function isSiblingJobCensusDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return window.localStorage.getItem('qm_disable_sibling_job_census') === 'true';
        }
    } catch { /* localStorage unavailable (SSR / Node tests) */ }
    return false;
}

// A job-folder NAME signal (Peter's corpus layout, 2026-08-19): quoting
// folders are named "<NN>_<Suburb> - <Street> - <Work>(<REF NNNN>)". A street
// -type token or a numeric job-reference parenthetical is a strong "this is a
// JOB, not a pack" marker — packs are named by CONTENT (Photos, RFQ Package),
// jobs are named by PLACE. Checked only after the CZ-pack and evidence-name
// exclusions, so "01. RFQ Package" can never reach these.
const JOB_STREET_TOKEN_RE = /\b(st|street|rd|road|ave|avenue|hwy|highway|ln|lane|pl|place|dr|drive|ct|court|cres|crescent|tce|terrace|pde|parade|blvd|boulevard|esplanade)\b/i;
const JOB_REFERENCE_RE = /\([^)]*\d{3,}[^)]*\)/;
const NUMBERED_PREFIX_RE = /^\d{1,3}[._\-\s]/;

async function censusSiblingJobFolders(entries: any[] = [], { limit = 2 } = {}) {
    if (isSiblingJobCensusDisabled()) return 0;
    const { directories } = directEntrySummary(entries);
    let count = 0;
    // Numbered-series fallback: a run of ≥3 numbered, non-pack, non-evidence
    // children is a filing SERIES of jobs (corpus folders, "wait for quoting"
    // parents) even when the names carry no street/reference token. Guarded
    // by a document/structure quorum so three photo-only "01_First visit"
    // leaves inside ONE job can never read as a series of jobs.
    let numberedCandidates = 0;
    let numberedDocQuorum = 0;
    for (const { name, handle } of directories) {
        if (matchProjectSubfolder(name)) continue;      // CZ pack, never a job
        if (looksLikeEvidenceDirectory(name)) continue; // Photos/Plans/…
        let childEntries: any[] = [];
        try { childEntries = await listEntries(handle); } catch { continue; }
        const signals = selectedFolderProjectSignals(childEntries);
        const { directories: childDirs, files: childFiles } = directEntrySummary(childEntries);
        const hasAnyContent = childDirs.length > 0 || childFiles.length > 0;
        const strong = signals.hasSyncManifest || hasCustomerZeroIntakeSubfolder(childEntries);
        const medium = hasBriefDirectFile(childFiles) && (
            childDirs.length > 0
            || childFiles.some(({ name: fileName }) => {
                const kind = classifyExplorerFile(fileName);
                return kind === 'image' || kind === 'video';
            })
        );
        // Name says JOB and the folder is not empty. Covers children whose
        // files all sit in nested pack subfolders (no direct brief to find).
        const nameSignal = hasAnyContent
            && (JOB_STREET_TOKEN_RE.test(String(name || '')) || JOB_REFERENCE_RE.test(String(name || '')));
        if (NUMBERED_PREFIX_RE.test(String(name || '')) && hasAnyContent) {
            numberedCandidates += 1;
            if (childDirs.length > 0 || hasBriefDirectFile(childFiles)) numberedDocQuorum += 1;
        }
        if (strong || medium || nameSignal) {
            count += 1;
            if (count >= limit) return count;
        }
    }
    if (count < limit && numberedCandidates >= 3 && numberedDocQuorum >= 2) return limit;
    return count;
}

function emptyCounts() {
    return {
        images: 0,
        documents: 0,
        emails: 0,
        textFiles: 0,
        videos: 0,
        audio: 0,
        unsupported: 0,
    };
}

function addKindCount(counts: any, kind: any) {
    if (kind === 'image') counts.images += 1;
    else if (kind === 'document') counts.documents += 1;
    else if (kind === 'email') counts.emails += 1;
    else if (kind === 'text') counts.textFiles += 1;
    else if (kind === 'video') counts.videos += 1;
    else if (kind === 'audio') counts.audio += 1;
    else counts.unsupported += 1;
}

export function supportedCount(counts: any = {}) {
    return (counts.images || 0)
        + (counts.documents || 0)
        + (counts.emails || 0)
        + (counts.textFiles || 0)
        + (counts.videos || 0)
        + (counts.audio || 0);
}

async function listEntries(directoryHandle: any) {
    const entries: any[] = [];
    if (!directoryHandle) return entries;

    if (typeof directoryHandle.entries === 'function') {
        for await (const [name, handle] of directoryHandle.entries()) {
            entries.push([name, handle]);
        }
    } else if (typeof directoryHandle.values === 'function') {
        for await (const handle of directoryHandle.values()) {
            entries.push([handle?.name, handle]);
        }
    }

    entries.sort(([aName], [bName]) => COLLATOR.compare(aName || '', bName || ''));
    return entries;
}

function attachRelativePath(file: any, relativePath: any, projectName: any, sourceRecord: any = null) {
    if (!file || !relativePath) return file;
    const descriptors = {
        relativePath,
        quoteMakerSourcePath: relativePath,
        quoteMakerPendingProject: projectName || '',
        // Preserve the File Explorer's project-namespaced identity when a
        // scan record becomes a browser File.  The pre-process row builder
        // uses this value as the document/photo/video id, which keeps a queued
        // selectedFileIds scope meaningful after materialisation.  Without it
        // the builder minted unrelated random ids and a scoped queue run either
        // skipped every selected file or silently widened to the whole project.
        ...(typeof sourceRecord?.id === 'string' && sourceRecord.id.trim()
            ? { quoteMakerFileId: sourceRecord.id.trim() }
            : {}),
        ...(typeof sourceRecord?.class === 'string' ? { class: sourceRecord.class } : {}),
        ...(typeof sourceRecord?.confidence === 'number' || sourceRecord?.confidence === null
            ? { confidence: sourceRecord.confidence }
            : {}),
        ...(Array.isArray(sourceRecord?.evidence) || sourceRecord?.evidence === null
            ? { evidence: Array.isArray(sourceRecord.evidence) ? [...sourceRecord.evidence] : null }
            : {}),
        ...(typeof sourceRecord?.classSource === 'string' ? { classSource: sourceRecord.classSource } : {}),
    };

    for (const [key, value] of Object.entries(descriptors)) {
        try {
            Object.defineProperty(file, key, {
                value,
                configurable: true,
                enumerable: false,
            });
        } catch {
            try { file[key] = value; } catch { /* read-only browser File */ }
        }
    }

    try {
        Object.defineProperty(file, 'webkitRelativePath', {
            value: relativePath,
            configurable: true,
            enumerable: false,
        });
    } catch {
        // Browser File.webkitRelativePath is read-only on some implementations.
    }

    return file;
}

/**
 * How many file handles to open at once while reading directory metadata.
 *
 * The scan needs `size` and `lastModified` for every supported file, and the
 * File System Access API only exposes those through getFile() — one real file
 * open each. Those opens used to run STRICTLY SERIALLY, so a folder cost
 * N x per-file-latency before the operator saw anything.
 *
 * Measured on the running dev build against a mock directory (2026-08-20),
 * which is what turned "clicking a project folder takes a long time to respond"
 * into a number:
 *
 *     20 files @ 3ms   ->   85 ms      max concurrent opens: 1
 *     60 files @ 3ms   ->  290 ms      max concurrent opens: 1
 *    200 files @ 3ms   ->  953 ms      max concurrent opens: 1
 *     60 files @ 25ms  -> 1557 ms      max concurrent opens: 1
 *
 * Dead linear, and 3ms is optimistic: RJ Bird's job folders live on a synced
 * drive, where a cold open is tens of milliseconds. A 200-photo folder at 25ms
 * is five seconds of nothing.
 *
 * 12 is deliberately modest. The win is the jump off 1 (12x), and a large pool
 * against a network or cloud-backed drive risks thrashing the very filesystem
 * we are waiting on.
 */
const FILE_METADATA_CONCURRENCY = 12;

/**
 * Open the supported files of ONE directory concurrently and return their
 * metadata by name. Errors are captured per file, never thrown, so the caller
 * replays each one exactly where the serial version would have hit it and the
 * warning/skip bookkeeping is unchanged.
 *
 * The eligibility test mirrors the loop's own conditions exactly — file kind,
 * not a generated QuoteMaker artefact, not unsupported — so this opens the SAME
 * set the serial code opened, never a byte more.
 */
async function readEntryMetadata(entries: any[], budget: number): Promise<Map<string, any>> {
    const queue: any[] = [];
    for (const [entryName, handle] of entries) {
        if (queue.length >= budget) break;
        const name = entryName || handle?.name || '';
        if (!handle || handle.kind !== 'file' || !name) continue;
        if (isGeneratedQuoteMakerFile(name)) continue;
        if (classifyExplorerFile(name) === 'unsupported') continue;
        queue.push([name, handle]);
    }

    const out = new Map<string, any>();
    if (queue.length === 0) return out;

    let cursor = 0;
    const worker = async () => {
        while (cursor < queue.length) {
            const index = cursor++;
            const entry = queue[index];
            if (!entry) continue;
            const [name, handle] = entry;
            try { out.set(name, { file: await handle.getFile(), error: null }); }
            catch (err) { out.set(name, { file: null, error: err }); }
        }
    };
    await Promise.all(
        Array.from({ length: Math.min(FILE_METADATA_CONCURRENCY, queue.length) }, worker),
    );
    return out;
}

async function scanFilesInDirectory(directoryHandle: any, {
    basePath = '',
    depth = 0,
    maxDepth = DEFAULT_SCAN_DEPTH,
    maxFiles = 300,
    files = [],
    counts = emptyCounts(),
    skipped = { directories: 0, files: 0, generated: 0 },
    warnings = [],
}: any = {}) {
    if (!directoryHandle || depth > maxDepth || files.length >= maxFiles) return { files, counts, skipped, warnings };

    let entries = [];
    try {
        entries = await listEntries(directoryHandle);
    } catch (err) {
        skipped.directories += 1;
        warnings.push({
            path: basePath || directoryHandle?.name || '',
            message: errorMessage(err),
        });
        return { files, counts, skipped, warnings };
    }

    // Open this directory's supported files concurrently BEFORE walking it, so
    // the loop below never blocks on a per-file disk read. Bounded by the
    // remaining file budget so it opens no more than the loop would consume.
    const entryMetadata = await readEntryMetadata(entries, Math.max(0, maxFiles - files.length));

    for (const [entryName, handle] of entries) {
        if (!handle || files.length >= maxFiles) break;
        const name = entryName || handle.name || '';
        if (!name) continue;
        const relativePath = basePath ? `${basePath}/${name}` : name;

        if (handle.kind === 'directory') {
            if (IGNORED_DIRECTORIES.has(name.toLowerCase())) {
                skipped.directories += 1;
                continue;
            }
            if (depth >= maxDepth) {
                // FE-SUBFOLDER-1: previously this skipped silently (only
                // bumping skipped.directories), so any files nested below the
                // depth cap vanished with no operator-visible reason. Push a
                // warning so FileExplorer's scan-warning banner surfaces
                // "N files in deeply-nested folders were skipped" instead of
                // dropping them quietly. The cap itself is now DEFAULT_SCAN_DEPTH
                // (8), raised from the historic 5.
                skipped.directories += 1;
                warnings.push({
                    path: relativePath,
                    message: `Folder is nested deeper than ${maxDepth} levels - its files were skipped. Move it closer to the project root, then rescan.`,
                });
                continue;
            }
            try {
                await scanFilesInDirectory(handle, {
                    basePath: relativePath,
                    depth: depth + 1,
                    maxDepth,
                    maxFiles,
                    files,
                    counts,
                    skipped,
                    warnings,
                });
            } catch (err) {
                skipped.directories += 1;
                warnings.push({ path: relativePath, message: errorMessage(err, name) });
            }
            continue;
        }

        if (handle.kind !== 'file') continue;
        if (isGeneratedQuoteMakerFile(name)) {
            skipped.generated += 1;
            continue;
        }

        const kind = classifyExplorerFile(name);
        let size = 0;
        let lastModified = 0;
        let readable = true;
        let readError = '';
        if (kind !== 'unsupported') {
            try {
                // Resolved by readEntryMetadata above. The fallback keeps this
                // correct if the budget cut the prefetch short, and a captured
                // error is re-thrown HERE so the catch below records the same
                // warning and skip count the serial version did.
                const prefetched = entryMetadata.get(name);
                if (prefetched?.error) throw prefetched.error;
                const file = prefetched?.file ?? await handle.getFile();
                size = file?.size || 0;
                lastModified = file?.lastModified || 0;
                // 2026-05-06 cloud-stub heuristic: a 0-byte email is almost
                // certainly a OneDrive / iCloud / Dropbox cloud-only file
                // whose bytes haven't hydrated yet, OR a sync-in-progress
                // placeholder. Real .msg / .eml files always carry a
                // CFB/MIME header (≥ a few KB). Surface as a soft failure
                // so the explorer flags it instead of silently showing
                // "0 KB" — which Peter saw on a 314 KB RFQ email at the
                // RJ Bird Artarmon job, with no inline reason visible.
                if (size === 0 && kind === 'email') {
                    readable = false;
                    readError = 'Email shows 0 bytes - likely a cloud-only file. Open the folder in File Explorer, right-click → Always keep on this device, then rescan.';
                    skipped.files += 1;
                    warnings.push({ path: relativePath, message: readError });
                }
            } catch (err) {
                readable = false;
                readError = errorMessage(err, name);
                skipped.files += 1;
                warnings.push({ path: relativePath, message: readError });
            }
        }

        addKindCount(counts, kind);
        files.push({
            id: relativePath,
            name,
            relativePath,
            kind,
            ext: extname(name),
            size,
            lastModified,
            readable,
            readError,
            // 2026-05-23 (Ask 3): every file defaults to UNSELECTED so the
            // user picks what comes in rather than the app pre-selecting
            // the whole intake bucket. Per Peter's E2E session: "when
            // going to file explorer, it is currently defaulting to
            // selecting all files and subfolders and subfolders' files as
            // the default behaviour. however, I want the default to have
            // no files selected." The Select-All affordance already
            // cascades through subfolders (FileExplorer.jsx::setAllSupported),
            // and the per-subfolder tri-state machinery
            // (FileExplorer.jsx::subfolderSelectionState) already handles
            // partial deselection cleanly — no other change needed here.
            //
            // The auto-select discipline that previously distinguished
            // emails (always preselected because the RFQ brief is
            // load-bearing) is also dropped; the Select-All button now
            // makes that choice explicit. `materializeExplorerFiles()`
            // still warns on unreadable selections at import time, so
            // the email-readability nuance is preserved at the import gate.
            //
            // Previous default-selection helper retained at
            // `defaultSelectionForRelativePath()` (still used by tests +
            // by callers that want the legacy "intake-aware" suggestion
            // independent of the per-file boolean stamped here).
            selectedByDefault: false,
            handle,
        });
    }

    return { files, counts, skipped, warnings };
}

// PB-CP-3 (file-explorer-redesign 2026-05-11): adapter for the
// FileSystemObserver's unknown-event subtree rewalk. Walks the given
// subtree handle and returns rows in the bulkUpsertFiles shape, with
// `relPath` reconstructed relative to the ROOT (not the subtree).
//
// The standard scanFilesInDirectory returns rich legacy shape with
// `relativePath` scoped to the subtree. The observer needs root-relative
// relPath so fe_file rows align with what the producer-side walker
// writes during a full walk. This function does the prefix join.
//
// `parentDirPrefix` is the relPath from the root to the subtree handle
// (e.g. 'Quotes/Stanley Street SP54295'). Each emitted row's relPath
// becomes `${parentDirPrefix}/${file.relativePath}` — same shape as
// _flattenProjectsToIndexRows produces during full walks.
//
// Underscore prefix marks this as observer-internal; not part of the
// public file-explorer-intake.js API.
export async function _scanFilesInDirectoryForObserver(subtreeHandle: any, parentDirPrefix = '') {
    if (!subtreeHandle) return [];
    const counts = emptyCounts();
    const { files } = await scanFilesInDirectory(subtreeHandle, {
        // FE-SUBFOLDER-1: share the raised canonical depth so observer
        // subtree rewalks collect the same files as the picker/drop walks.
        maxDepth: DEFAULT_SCAN_DEPTH,
        // Subtree rewalks should NOT impose the legacy 300-cap since
        // Phase A removed it. Use Infinity so a recursive copy of 1000
        // files into a project still gets fully indexed.
        maxFiles: Number.POSITIVE_INFINITY,
        counts,
    });
    const rows = [];
    for (const file of (Array.isArray(files) ? files : [])) {
        const fileRel = String(file?.relativePath || file?.name || '');
        if (!fileRel) continue;
        const fullRel = parentDirPrefix
            ? `${parentDirPrefix}/${fileRel}`
            : fileRel;
        const lastSlash = fullRel.lastIndexOf('/');
        const parentDir = lastSlash >= 0 ? fullRel.slice(0, lastSlash) : '';
        rows.push({
            relPath: fullRel,
            name: file?.name ?? fullRel.slice(lastSlash + 1),
            parentDir,
            ext: typeof file?.ext === 'string' ? file.ext : null,
            kind: typeof file?.kind === 'string' ? file.kind : 'unsupported',
            sizeBytes: Number.isFinite(file?.size) ? file.size : 0,
            mtime: Number.isFinite(file?.lastModified) ? file.lastModified : 0,
            docClass: null, // observer doesn't classify; caller can re-run classifier later
            docClassifiedAt: null,
            contentHash: null,
        });
    }
    return rows;
}

// BQ-CP-2 / DEC-BQ-014 (Phase 1B M1.4, 2026-05-03): content-aware buildSignals.
// Reads each document file's `class` (set by classifyDocumentsInProject below
// — either from a v4 manifest's persisted classification, or from filename-
// only classification at scan time). Emits stronger signals than the legacy
// folder-name regex because we know WHAT each PDF actually is, not just that
// the folder name contains "rfq".
//
// The folder-name fallback survives so unsynced folders without classification
// (e.g. when `qm_disable_explorer_content_scan` is on) still produce a signal.
export function buildSignals(candidate: any) {
    const signals = [];
    const counts = candidate.counts || {};
    const name = String(candidate.name || '');
    const files = Array.isArray(candidate.files) ? candidate.files : [];

    // Aggregate per-class counts from classified documents (M1.3 output)
    const classCounts = { rfq: 0, our_past_quote: 0, competitor_quote: 0, estimator_notes: 0, plan: 0, report: 0, other: 0, unknown: 0 };
    for (const f of files) {
        if (f && typeof f.class === 'string' && Object.prototype.hasOwnProperty.call(classCounts, f.class)) {
            (classCounts as any)[f.class] += 1;
        }
    }

    // Content-derived signals (HIGHEST priority — concrete evidence)
    if (classCounts.rfq > 0) {
        signals.push(`${classCounts.rfq} RFQ document${classCounts.rfq > 1 ? 's' : ''}`);
    }
    if (classCounts.plan > 0) {
        signals.push(`${classCounts.plan} plan/drawing${classCounts.plan > 1 ? 's' : ''}`);
    }
    if (classCounts.report > 0) {
        signals.push(`${classCounts.report} report${classCounts.report > 1 ? 's' : ''}`);
    }
    if (classCounts.our_past_quote > 0) signals.push('past quote (ours)');
    if (classCounts.competitor_quote > 0) signals.push('competitor quote');
    if (classCounts.estimator_notes > 0) signals.push('estimator notes');

    // Folder-name fallback — only when no content-derived RFQ signal fired.
    if (classCounts.rfq === 0 && /\b(rfq|request|quote|tender|scope|works?)\b/i.test(name)) {
        signals.push('RFQ name signal');
    }

    // Generic file-type signals (independent of classification)
    const totalDocs = (counts.documents || 0) + (counts.emails || 0);
    if (totalDocs > 0 && classCounts.rfq === 0 && classCounts.plan === 0 && classCounts.report === 0) {
        signals.push('brief documents');
    }
    if ((counts.images || 0) > 0) signals.push('site photos');
    if ((counts.videos || 0) > 0) signals.push('walkthrough video');
    if ((counts.textFiles || 0) > 0) signals.push('site notes');
    return signals;
}

// BQ-CP-2 / DEC-BQ-014 (Phase 1B M1.3, 2026-05-03): scan-time per-document
// classification. The scan reads no file content, so the only class it can
// give is one a synced folder's v4 manifest stored at import time (set there
// by the content classifier). Every other document is 'unknown' until it is
// imported and its text is read. CN6 removed the file-name pass that used to
// run here (Peter 2026-10-13: "file names shouldnt dictate anything. only
// the contents.").
//
// Behind kill switch `qm_disable_explorer_content_scan` (default: scan ON).
//
// Mutates the file objects in-place — adds `class`, `confidence`, `evidence`,
// `classSource` ('validated-manifest' | 'manifest' | 'scan-unread'). A parsed
// but unknown/legacy manifest stays a weak hint, and only a recognised schema
// tied to a durable project id is authoritative. The manifest record is found
// by the file's name because the manifest itself lists the files QuoteMaker
// wrote. That is the file's identity, not its class.

function isExplorerContentScanDisabled() {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('qm_disable_explorer_content_scan') === 'true';
        }
    } catch { /* localStorage unavailable */ }
    return false;
}

async function classifyDocumentsInProject(files: any, manifest: any) {
    if (!Array.isArray(files) || files.length === 0) return files;
    if (isExplorerContentScanDisabled()) return files;

    // Build a lookup table from manifest documents[] keyed by both fileName
    // and originalName so we can find a persisted classification regardless
    // of whether the on-disk file uses the renamed sync filename or the raw.
    const manifestDocs = Array.isArray(manifest?.documents) ? manifest.documents : [];
    const manifestVersion = Number(manifest?.version);
    const manifestClassificationSource = Number.isInteger(manifestVersion)
        && manifestVersion >= 4
        && manifestVersion <= SYNC_MANIFEST_SCHEMA_VERSION
        && typeof manifest?.projectId === 'string'
        && manifest.projectId.trim().length > 0
        ? 'validated-manifest'
        : 'manifest';
    const manifestByName = new Map();
    for (const md of manifestDocs) {
        if (md?.fileName) manifestByName.set(md.fileName, md);
        if (md?.originalName) manifestByName.set(md.originalName, md);
    }

    for (const file of files) {
        if (!file) continue;
        if (file.kind !== 'document' && file.kind !== 'email') continue;

        // 1. Persisted class from v4 manifest (preferred — was classified at
        //    import time from the document's text).
        const persisted = manifestByName.get(file.name) || manifestByName.get(file.relativePath);
        if (persisted && typeof persisted.class === 'string' && persisted.class !== 'unknown') {
            file.class = persisted.class;
            file.confidence = typeof persisted.confidence === 'number' ? persisted.confidence : null;
            file.evidence = Array.isArray(persisted.evidence) ? persisted.evidence : null;
            file.classSource = manifestClassificationSource;
            continue;
        }

        // 2. No text is read at scan, so the class is not known yet. Before
        //    CN6 the scan classified each file from its name alone. Since CN2
        //    and CN3 a name could not make a file the RFQ, a report or a
        //    drawing, so that pass gave every file 'other' (measured on 2,944
        //    of RJ Bird's file names). It no longer reads the name: the class
        //    comes from the text when the file is imported.
        file.class = 'unknown';
        file.confidence = null;
        file.evidence = null;
        file.classSource = 'scan-unread';
    }

    return files;
}

// ─── Project status surfacing (Peter 2026-05-03) ────────────────────────────
//
// Each synced project folder carries a `SyncManifest.json` written by
// SyncFolderService.syncProject(). Reading it lets us show
// completed/in-progress status badges in the File Explorer sidebar
// without round-tripping to the in-app database.
//
// Manifest shape (see sync-folder.js writer):
//   {
//     version, projectId, folderName, displayName, lastSyncAt,
//     keyFields: { quoteRef, requesterCompany, contact, siteAddress,
//                  strataPlan, status, currentStage },
//     photos: [...], documents: [...], videos: [...],
//   }
//
// Status canonicalisation — match postprocessing-pipeline.js:367 which
// is the source of truth: wonAt → 'won', lostAt → 'lost', emailSent → 'sent',
// otherwise 'draft'. The manifest stores the same string directly.

const STATUS_ALIASES = {
    won: 'won',
    accepted: 'won',
    lost: 'lost',
    declined: 'lost',
    sent: 'sent',
    emailed: 'sent',
    quoted: 'quoted',
    in_progress: 'quoted',
    'in-progress': 'quoted',
    draft: 'draft',
    pending: 'draft',
    // BQ-CP-2 / DEC-BQ-003 (2026-05-03): the new state emitted by Phase 2's
    // batch processor when Stage 5 completes. AI work done; builder needs to
    // review + print + send. Wired across STATUS_ALIASES + labelMap (this file)
    // and STATUS_TONE + STATUS_FILTERS + counter (FileExplorer.jsx) per the
    // schema-drift-detector audit (INVESTIGATION-REPORT.md §2).
    ready_for_builder_review: 'ready_for_builder_review',
    'ready-for-builder-review': 'ready_for_builder_review',
    builder_review: 'ready_for_builder_review',
    awaiting_review: 'ready_for_builder_review',
    // DEC-TQ-001 (2026-05-06): pre-processing terminal state. Set by the
    // pre-processing runner when intake completes (text extracted, photos
    // persisted). Means "AI hasn't started yet — ready when the user clicks
    // Process prepare-quote (or auto-promote fires)".
    ready_for_analysis: 'ready_for_analysis',
    'ready-for-analysis': 'ready_for_analysis',
    intake_complete: 'ready_for_analysis',
    preprocessed: 'ready_for_analysis',
};

/**
 * Normalise an arbitrary status string into the canonical set:
 *   'won' | 'lost' | 'sent' | 'quoted' | 'draft' |
 *   'ready_for_analysis' | 'ready_for_builder_review'
 * Returns null if the input is empty so callers can render an "unsynced"
 * badge instead.
 */
export function normaliseProjectStatus(raw: any) {
    if (typeof raw !== 'string') return null;
    const key = raw.trim().toLowerCase();
    if (!key) return null;
    return (STATUS_ALIASES as any)[key] || 'draft';
}

/**
 * Try to read SyncManifest.json from a project folder. Returns the parsed
 * object or null if the file is missing / unreadable / malformed. Always
 * non-throwing — a missing manifest means the folder isn't a synced project.
 */
async function readSyncManifest(projectHandle: any) {
    if (!projectHandle || projectHandle.kind !== 'directory') return null;
    try {
        const manifestHandle = await projectHandle.getFileHandle('SyncManifest.json');
        const file = await manifestHandle.getFile();
        const text = await file.text();
        if (!text) return null;
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
    } catch {
        return null;
    }
}

/**
 * Project metadata derived from the sync manifest. Returns a lean object
 * that can sit on the project record without bloating it.
 */
function deriveProjectMeta(manifest: any) {
    if (!manifest) {
        return {
            isSynced: false,
            status: null,
            statusLabel: 'Unsynced',
            currentStage: null,
            quoteRef: '',
            requesterCompany: '',
            contact: '',
            siteAddress: '',
            strataPlan: '',
            companyId: null,
            propertyId: null,
            contactId: null,
            lastSyncAt: null,
            projectId: null,
            displayName: null,
        };
    }
    const k = manifest.keyFields || {};
    const status = normaliseProjectStatus(k.status);
    const labelMap = {
        won: 'Won',
        lost: 'Lost',
        sent: 'Sent',
        quoted: 'Quoted',
        draft: 'In progress',
        // BQ-CP-2 / DEC-BQ-003 (2026-05-03): label for the new handoff state.
        ready_for_builder_review: 'Ready for review',
        // DEC-TQ-001 (2026-05-06): label for pre-processing terminal state.
        ready_for_analysis: 'Ready for analysis',
    };
    return {
        isSynced: true,
        status,
        statusLabel: status ? (labelMap as any)[status] : 'Synced',
        currentStage: Number.isFinite(k.currentStage) ? k.currentStage : null,
        quoteRef: String(k.quoteRef || ''),
        requesterCompany: String(k.requesterCompany || ''),
        contact: String(k.contact || ''),
        siteAddress: String(k.siteAddress || ''),
        strataPlan: String(k.strataPlan || ''),
        // BQ-CP-2 / DEC-BQ-012 (2026-05-03): canonical CRM IDs from v4 manifest.
        // v3 manifests resolve to null (forward-compat M1.2). The file explorer
        // can use these to pivot to the CRM Workbench Sites/Requesters mode.
        companyId: k.companyId || null,
        propertyId: k.propertyId || null,
        contactId: k.contactId || null,
        lastSyncAt: manifest.lastSyncAt || null,
        projectId: manifest.projectId || null,
        displayName: manifest.displayName || manifest.folderName || null,
    };
}

export async function scanExplorerProject(projectHandle: any, options: any = {}) {
    const projectPath = normaliseExplorerPath(
        options.projectPath
        || options.relativePath
        || projectHandle?.name
        || ''
    );
    const syncRelativePath = options.syncRelativePath === ''
        ? ''
        : normaliseExplorerPath(
            options.syncRelativePath
            || options.projectPath
            || options.relativePath
            || projectHandle?.name
            || ''
        );
    const counts = emptyCounts();
    const { files, skipped, warnings } = await scanFilesInDirectory(projectHandle, {
        // FE-SUBFOLDER-1: default per-project scan depth raised 5 → 8
        // (DEFAULT_SCAN_DEPTH). Caller can still override via options.maxDepth.
        maxDepth: options.maxDepth ?? DEFAULT_SCAN_DEPTH,
        maxFiles: options.maxFilesPerProject ?? 300,
        counts,
    });
    const sortedFiles = files.sort((a: any, b: any) => {
        const kindOrder = { document: 0, email: 1, text: 2, image: 3, video: 4, audio: 5, unsupported: 6 };
        return ((kindOrder as any)[a.kind] ?? 9) - ((kindOrder as any)[b.kind] ?? 9)
            || COLLATOR.compare(a.relativePath || a.name, b.relativePath || b.name);
    });
    // Read sync manifest in parallel-ish (already awaited file enumeration).
    // Failures are swallowed inside readSyncManifest — non-synced folders
    // simply get isSynced:false.
    const manifest = await readSyncManifest(projectHandle);
    const meta = deriveProjectMeta(manifest);
    // BQ-CP-2 / DEC-BQ-014 (M1.3, 2026-05-03): per-document classification —
    // mutates sortedFiles in place to add `class` / `confidence` / `evidence`
    // / `classSource`. Persisted manifest classifications are preferred;
    // unsynced docs get filename-only classification (cheap, capped at 10/fldr).
    // Behind kill switch `qm_disable_explorer_content_scan`.
    await classifyDocumentsInProject(sortedFiles, manifest);
    // DEC-BQ-025 fix (v5.19.22): for synced folders, use the Dexie project
    // UUID (from SyncManifest.projectId) as the primary id so downstream
    // consumers — especially batch-queue.enqueue(projectId) → orchestrator
    // pipeline-runner.loadProject which calls db.projects.get(projectId) —
    // receive a value that resolves in Dexie. Pre-fix, every synced folder
    // got `id = folder.name` (a long descriptive string), and every queue
    // run failed instantly with "Project ... not found in Dexie".
    // Unsynced folders fall back to folder name; the FileExplorer queue UI
    // gates them out with a UUID-shape guard so they never reach enqueue.
    const projectScope = meta.projectId || projectHandle?.name || `project_${Date.now()}`;

    // FE bug-fix (v5.19.58 — Peter 2026-05-12): namespace every file.id with
    // the project's stable scope so two project folders that share an internal
    // subfolder name (e.g. both have "01. RFQ Package/RFQ.pdf" — the
    // Customer-Zero layout where every project has the same 10 numbered
    // subfolders) do NOT produce identical ids that collide in
    // `selectedFileIds.has(file.id)` lookups. The id stays opaque for
    // consumers (Set membership, React keys); relativePath is unchanged so
    // anything that needs the path (materializeExplorerFiles, search, etc.)
    // still reads it from `relativePath`. See
    // docs/file-explorer-folder-convention/findings/same-named-subfolder-selection-bug.md
    for (const f of sortedFiles) {
        const relPath = f.relativePath || f.name || '';
        f.id = `${projectScope}::${relPath}`;
    }

    const candidate = {
        id: projectScope,
        folderName: projectHandle?.name || '',
        name: projectHandle?.name || 'Folder',
        path: projectPath || projectHandle?.name || '',
        relativePath: projectPath || projectHandle?.name || '',
        sourcePath: projectPath || projectHandle?.name || '',
        syncRelativePath,
        handle: projectHandle,
        files: sortedFiles,
        counts,
        fileCount: files.length,
        selectedCount: files.filter((file: any) => file.selectedByDefault).length,
        supportedCount: supportedCount(counts),
        skipped,
        scanWarnings: warnings,
        scanError: warnings.length > 0 ? warnings[0].message : '',
        truncated: files.length >= (options.maxFilesPerProject ?? 300),
        meta, // Project status + key fields from SyncManifest.json
    };
    return { ...candidate, signals: buildSignals(candidate) };
}

function failedExplorerProject(handle: any, name: any, message: any, {
    relativePath = '',
    depth = 1,
    selectedFolderSignals = null,
    folderRole = projectFolderRoleForDepth(depth),
    requiresProjectConfirmation = false,
}: any = {}) {
    const label = handle?.name || name || 'Unreadable folder';
    const path = normaliseExplorerPath(relativePath || label);
    return {
        id: handle?.name || name || `failed_${Date.now()}`,
        name: label,
        folderName: label,
        path,
        relativePath: path,
        sourcePath: path,
        handle,
        files: [],
        counts: emptyCounts(),
        fileCount: 0,
        selectedCount: 0,
        supportedCount: 0,
        skipped: { directories: 1, files: 0, generated: 0 },
        scanWarnings: [{ path, message }],
        scanError: message,
        truncated: false,
        signals: [],
        meta: deriveProjectMeta(null),
        folderRole,
        requiresProjectConfirmation,
        ...(selectedFolderSignals ? { selectedFolderSignals } : {}),
    };
}

// PA-CP-3 kill-switch readers. Defensive try/catch so SSR / Node tests don't crash.
function _readPhaseABoolFlag(key: any) {
    try {
        if (typeof localStorage === 'undefined') return false;
        return localStorage.getItem(key) === 'true';
    } catch {
        return false;
    }
}

// PA-CP-3 helper: flatten the legacy projects[] return shape into a flat
// list of fe_file row candidates suitable for bulkUpsertFiles. Each file's
// relPath is reconstructed relative to the picked parent (the root), not
// the project — so a file at <root>/Quotes/Stanley/photos/IMG.jpg gets
// relPath='Quotes/Stanley/photos/IMG.jpg' and parentDir='Quotes/Stanley/photos'.
function _flattenProjectsToIndexRows(projects: any) {
    const rows = [];
    for (const project of (Array.isArray(projects) ? projects : [])) {
        const projectPath = String(project?.path || project?.relativePath || project?.name || '');
        for (const file of (Array.isArray(project?.files) ? project.files : [])) {
            const fileRel = String(file?.relativePath || file?.name || '');
            if (!fileRel) continue;
            const fullRel = projectPath ? `${projectPath}/${fileRel}` : fileRel;
            const lastSlash = fullRel.lastIndexOf('/');
            const parentDir = lastSlash >= 0 ? fullRel.slice(0, lastSlash) : '';
            rows.push({
                relPath: fullRel,
                name: file?.name ?? fullRel.slice(lastSlash + 1),
                parentDir,
                ext: typeof file?.ext === 'string' ? file.ext : null,
                kind: typeof file?.kind === 'string' ? file.kind : 'unsupported',
                sizeBytes: Number.isFinite(file?.size) ? file.size : 0,
                mtime: Number.isFinite(file?.lastModified) ? file.lastModified : 0,
                docClass: typeof file?.class === 'string' ? file.class : null,
                docClassifiedAt: Number.isFinite(file?.classifiedAt) ? file.classifiedAt : null,
                contentHash: null, // populated lazily on upload (Phase G concern)
            });
        }
    }
    return rows;
}

// PA-CP-3 (2026-05-11): public scanExplorerParentFolder is now a wrapper
// that:
//   1. Reads the qm_force_legacy_walk + qm_disable_explorer_index_uncapped
//      kill switches.
//   2. Computes effective options — by default the 300-cap is REMOVED
//      (Phase A's headline value); only restored when the uncapped kill
//      switch is ON.
//   3. Delegates to the legacy walker body (now `_runLegacyParentFolderWalk`).
//   4. Best-effort writes the flattened result into the file-explorer index
//      via indexRoot (skipped when force-legacy-walk is ON).
//
// Returns the same legacy projects[] shape as before so App.jsx:8956 and
// Stage1DataEntry.jsx:1154 callers need no change. PA-CP-4 will flip
// FileExplorer.jsx to read from the index when a fresh walk is available.
export async function scanExplorerParentFolder(parentHandle: any, options: any = {}) {
    if (!parentHandle) return [];

    const forceLegacy = _readPhaseABoolFlag('qm_force_legacy_walk');
    const restoreCap = _readPhaseABoolFlag('qm_disable_explorer_index_uncapped');

    // Default: cap removed (Phase A headline). When uncapped-kill-switch
    // is ON: restore the legacy 300 cap. Caller can still override via
    // options.maxFilesPerProject.
    const effectiveMaxFiles = restoreCap
        ? (options.maxFilesPerProject ?? 300)
        : (options.maxFilesPerProject ?? Number.POSITIVE_INFINITY);

    const effectiveOptions = { ...options, maxFilesPerProject: effectiveMaxFiles };

    const projects = await _runLegacyParentFolderWalk(parentHandle, effectiveOptions);

    if (!forceLegacy) {
        try {
            const { indexRoot } = await import('./file-explorer-index');
            const flatFiles = _flattenProjectsToIndexRows(projects);
            await indexRoot(parentHandle, async () => flatFiles, { mode: 'full' });
        } catch (err) {
            console.warn('[FileExplorer] Index write failed (best-effort):', (err as any)?.message);
        }
    }

    return projects;
}

async function _runLegacyParentFolderWalk(parentHandle: any, options: any = {}) {
    if (!parentHandle) return [];
    const maxProjects = options.maxProjects ?? 120;
    const maxDiscoveryDepth = options.maxDiscoveryDepth ?? DEFAULT_DISCOVERY_DEPTH;
    const maxDirectories = options.maxDiscoveryDirectories ?? DEFAULT_DISCOVERY_DIRECTORY_LIMIT;
    let directoriesVisited = 0;
    const projects: any[] = [];
    const entries = await listEntries(parentHandle);
    const selectedFolderSignals = selectedFolderProjectSignals(entries);

    // Sibling-job census (2026-08-19): ≥2 unambiguous job children outrank
    // EVERY treat-as-one-project rule below — including a stray root
    // SyncManifest — so a parent of jobs always fragments into its jobs.
    const siblingJobCount = await censusSiblingJobFolders(entries);
    if (siblingJobCount >= 2 && selectedFolderSignals.hasSyncManifest) {
        console.info(
            `[FileExplorer] Selected folder carries a SyncManifest but holds ${siblingJobCount}+ sibling job folders — `
            + 'treating it as a parent of projects; the root manifest is ignored for grouping.',
        );
    }
    // FE-SUBFOLDER-1: the sync rule above protects the exact CZ layout +
    // high-evidence-ratio folders. The async smart-grouping pass below also
    // keeps a SELECTED folder whose children are evidence buckets (Photos/
    // Plans/Reports/Old/Misc/…) as ONE project even when its evidence ratio
    // is low and it has >4 subdirs — the case that previously fragmented into
    // one phantom project per child. shouldGroupFolderAsSingleProject returns
    // false (so we fall through to the legacy descend) the moment any child
    // looks like a sibling job or a structural parent-of-jobs.
    if (siblingJobCount < 2
        && (shouldTreatSelectedFolderAsProject(entries, options) || await shouldGroupFolderAsSingleProject(entries))) {
        try {
            const project = await scanExplorerProject(parentHandle, {
                ...options,
                projectPath: parentHandle?.name || '',
                syncRelativePath: '',
            });
            return [{
                ...project,
                folderRole: 'selected-folder-project',
                requiresProjectConfirmation: true,
                selectedFolderSignals,
            }];
        } catch (err) {
            const message = errorMessage(err);
            return [failedExplorerProject(parentHandle, parentHandle?.name, message, {
                relativePath: parentHandle?.name || '',
                depth: 0,
                selectedFolderSignals,
                folderRole: 'selected-folder-project',
                requiresProjectConfirmation: true,
            })];
        }
    }

    const visitDirectory = async (handle: any, name: any, relativePath: any, depth: any) => {
        if (!handle || projects.length >= maxProjects) return;
        if (directoriesVisited >= maxDirectories) return;
        directoriesVisited += 1;

        let childEntries = [];
        try {
            childEntries = await listEntries(handle);
        } catch (err) {
            const message = errorMessage(err, name);
            projects.push(failedExplorerProject(handle, name, message, {
                relativePath,
                depth,
                folderRole: projectFolderRoleForDepth(depth),
            }));
            return;
        }

        // Job-name signal (2026-08-19): a discovered folder whose OWN name
        // reads as a job — a street-type token or a numeric job-reference
        // parenthetical ("01_Ashfield - Benalla Ave - …(PR & AB 0226)") — IS
        // a project, full stop. Without this, a job whose files sit in a
        // doc-bearing pack subfolder ("Reference Files") was descended-into
        // and the PACK promoted as the project under the pack's name. This
        // also closes the recorded FE-SUBFOLDER-1 address edge ("5 Plans
        // Ave"): the address outranks the evidence-word misread.
        const dirName = String(name || handle?.name || '');
        const nameIsJob = childEntries.length > 0
            && !matchProjectSubfolder(dirName)
            && !looksLikeEvidenceDirectory(dirName)
            && (JOB_STREET_TOKEN_RE.test(dirName) || JOB_REFERENCE_RE.test(dirName));
        // FE-SUBFOLDER-1: same smart-grouping guard on the discovered path.
        // Without it, a job folder buried under the selected root with a low
        // evidence ratio + many subdirs would be descended-into and each of
        // its evidence subfolders promoted to a separate phantom project. The
        // grouping check keeps it as ONE project; it bails (false) the instant
        // a child looks like a sibling job or a structural parent-of-jobs, so
        // a true parent folder of sibling jobs still fragments correctly.
        if (
            (
                nameIsJob
                || shouldTreatDiscoveredFolderAsProject(childEntries, { ...options, allowSelectedFolderAsProject: true })
                || await shouldGroupFolderAsSingleProject(childEntries)
            )
            // Same census guard on the discovered path: a folder holding ≥2
            // unambiguous sibling jobs is a parent of projects even when a
            // stray manifest, a loose root file, or a job-like NAME says
            // otherwise — descend.
            && (await censusSiblingJobFolders(childEntries)) < 2
        ) {
            try {
                const project = await scanExplorerProject(handle, {
                    ...options,
                    projectPath: relativePath,
                    syncRelativePath: relativePath,
                });
                projects.push({
                    ...project,
                    folderRole: projectFolderRoleForDepth(depth),
                    requiresProjectConfirmation: false,
                });
            } catch (err) {
                const message = errorMessage(err, name);
                projects.push(failedExplorerProject(handle, name, message, {
                    relativePath,
                    depth,
                    folderRole: projectFolderRoleForDepth(depth),
                }));
            }
            return;
        }

        if (depth >= maxDiscoveryDepth) return;

        for (const [childName, childHandle] of childEntries) {
            if (projects.length >= maxProjects || directoriesVisited >= maxDirectories) break;
            if (!childHandle || childHandle.kind !== 'directory') continue;
            if (IGNORED_DIRECTORIES.has(String(childName || '').toLowerCase())) continue;
            await visitDirectory(
                childHandle,
                childName,
                joinExplorerPath(relativePath, childName || childHandle.name || ''),
                depth + 1
            );
        }
    };

    // Census override (2026-08-19): when the selected root fragments into its
    // sibling jobs but ITSELF carries a job (a manifest and/or loose files at
    // the root — the "completed quote folder that later gained sibling job
    // copies" layout), surface the ROOT as its own project too, scanned at
    // depth 0 so it owns ONLY its direct files. Without this, the root job's
    // files silently vanish from the Browse list.
    if (siblingJobCount >= 2
        && (selectedFolderSignals.hasSyncManifest || selectedFolderSignals.hasDirectSupportedFile)) {
        try {
            const rootProject = await scanExplorerProject(parentHandle, {
                ...options,
                maxDepth: 0,
                projectPath: parentHandle?.name || '',
                syncRelativePath: '',
            });
            if ((rootProject?.fileCount || 0) > 0) {
                projects.push({
                    ...rootProject,
                    folderRole: 'selected-folder-project',
                    requiresProjectConfirmation: true,
                    selectedFolderSignals,
                });
            }
        } catch (err) {
            console.warn('[FileExplorer] Root-files scan failed (best-effort):', errorMessage(err));
        }
    }

    for (const [name, handle] of entries) {
        if (!handle || handle.kind !== 'directory') continue;
        if (IGNORED_DIRECTORIES.has(String(name || '').toLowerCase())) continue;
        if (projects.length >= maxProjects) break;
        await visitDirectory(handle, name, name || handle.name || '', 1);
    }

    return projects.sort((a, b) => (
        COLLATOR.compare(a.path || a.name || '', b.path || b.name || '')
    ));
}

export function explorerFolderWarningCount(projects: any) {
    return (Array.isArray(projects) ? projects : [])
        .reduce((total, project) => total + (project?.scanWarnings?.length || 0), 0);
}

export function explorerFolderWarningSummary(projects: any) {
    const count = explorerFolderWarningCount(projects);
    if (count <= 0) return '';
    return `${count} folder/file read issue${count === 1 ? '' : 's'} skipped. Check access, then rescan if something is missing.`;
}

export function canPickExplorerParentFolder(win = typeof window !== 'undefined' ? window : undefined) {
    return hasDirectoryPicker(win);
}

export async function pickExplorerParentFolder(win = typeof window !== 'undefined' ? window : undefined) {
    if (!canPickExplorerParentFolder(win)) {
        throw new Error(getFolderAccessUnsupportedMessage('File Explorer'));
    }
    try {
        const handle = await (win as any).showDirectoryPicker({
            mode: 'read',
            startIn: 'documents',
        });
        return { name: handle?.name || 'Sync root', handle };
    } catch (err) {
        if ((err as any)?.name === 'AbortError') throw err;
        throw normaliseDirectoryPickerError(err, 'File Explorer', win);
    }
}

/**
 * Partition selected file records (or materialized File objects) by the
 * SHALLOWEST path depth at which they differentiate, beneath an optional
 * `pathPrefix`. Used by the import flow to detect when a single click would
 * bundle files from multiple "leaf" folders into one Stage 1 import — the
 * Frankenstein-project bug Peter reported 2026-05-06.
 *
 * 2026-05-06 (claude/modest-brown-0e10cd): rewrote from first-segment-only
 * grouping to depth-aware grouping. The original algorithm fired only when
 * the first path segment differed, which missed deep-parent-sync cases where
 * the user syncs a category folder and selects from sibling project leaves
 * underneath. Example failure with the old algorithm:
 *   - sync root contains "Wait for Quoting/" with 5+ project subfolders
 *   - all selections share first segment "Wait for Quoting" → count=1
 *   - ConfirmModal never fired; the user got a 125-file Frankenstein project
 * The depth-aware algorithm walks the path level by level and uses the
 * shallowest depth at which ≥2 distinct prefixes appear. For the failure case
 * above, depth=2 differentiates {"Wait for Quoting/Artarmon", "Wait for
 * Quoting/Naremburn", ...} → count=N → modal fires.
 *
 * Inputs:
 *   - records — either scan-records (with `relativePath` plain key) or
 *     materialized File objects (with `relativePath` set via attachRelativePath).
 *   - pathPrefix — optional currentPath prefix the user is viewing (e.g.
 *     "Wait for Quoting/Artarmon/"). Files outside this prefix are ignored.
 *     A file directly inside the prefix gets group "__root__"; a file in a
 *     subdirectory beneath the prefix participates in the depth-aware partition.
 *
 * Returns: { groups: Map<string, record[]>, count: number, distinctFolders: string[] }
 *
 * Group keys are FULL paths from the (post-prefix) root, e.g.
 * "Wait for Quoting/Artarmon - Hampden Rd". For a clean display label,
 * extract the basename (last segment after the final "/") at the call site.
 *
 * `count` excludes "__root__" because root-level files don't conflict with
 * folder grouping (they're typically the parent's own RFQ at top level).
 * `distinctFolders` is sorted ascending by name.
 */
export function partitionExplorerFilesByFolder(records: any, { pathPrefix = '' } = {}) {
    const groups = new Map();
    const arr = Array.isArray(records) ? records : [];
    const rootFiles = [];
    const subfolderFiles = [];

    for (const rec of arr) {
        if (!rec) continue;
        const rel = rec.relativePath || rec.webkitRelativePath || rec.name || '';
        if (pathPrefix && !rel.startsWith(pathPrefix)) continue;
        const remainder = pathPrefix ? rel.slice(pathPrefix.length) : rel;
        const slash = remainder.indexOf('/');
        if (slash === -1) {
            rootFiles.push(rec);
        } else {
            // Split into [folder1, folder2, ..., filename]; drop the filename
            // and any empty segments (defensive for trailing-slash edge cases).
            const parts = remainder.split('/');
            const segments = parts.slice(0, -1).filter(Boolean);
            if (segments.length === 0) {
                rootFiles.push(rec);
            } else {
                subfolderFiles.push({ rec, segments });
            }
        }
    }

    if (rootFiles.length > 0) {
        groups.set('__root__', rootFiles);
    }

    if (subfolderFiles.length > 0) {
        // Find the SHALLOWEST depth at which subfolderFiles differentiate
        // (≥ 2 distinct prefixes), bounded by `minDepth` — the shortest
        // segments.length across the selection. Beyond minDepth, any
        // "differentiation" is between sibling subfolders WITHIN a single
        // project leaf (e.g. Artarmon/photos vs Artarmon/plans), which we
        // do NOT want to flag as multi-project.
        const minDepth = Math.min(...subfolderFiles.map(f => f.segments.length));
        let chosenDepth = 0;
        for (let depth = 1; depth <= minDepth; depth += 1) {
            const distinct = new Set();
            for (const f of subfolderFiles) {
                distinct.add(f.segments.slice(0, depth).join('/'));
            }
            if (distinct.size >= 2) {
                chosenDepth = depth;
                break;
            }
        }
        if (chosenDepth === 0) {
            // No differentiation up to minDepth → single project. Use the
            // longest common prefix (= the depth-`minDepth` slice of any
            // file, since they all agree at every depth up to minDepth).
            const lcpKey = subfolderFiles[0]!.segments.slice(0, minDepth).join('/');
            groups.set(lcpKey, subfolderFiles.map(f => f.rec));
        } else {
            for (const f of subfolderFiles) {
                const key = f.segments.slice(0, chosenDepth).join('/');
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(f.rec);
            }
        }
    }

    const distinctFolders = Array.from(groups.keys())
        .filter(k => k !== '__root__')
        .sort((a, b) => COLLATOR.compare(a, b));
    return { groups, count: distinctFolders.length, distinctFolders };
}

/**
 * Split a selection into in-scope (under pathPrefix) and out-of-scope buckets.
 * Used by the FileExplorer to surface "X selected here, Y elsewhere" and to
 * power the "Keep only here" trim-selection button.
 *
 * Empty pathPrefix means "the project root" — all selections are in scope.
 *
 * 2026-05-06 (claude/modest-brown-0e10cd): added for Bug A+B fix where the
 * user navigates deep into a subfolder, sees only that folder's files, but
 * the selection (set when entering the parent project) silently includes
 * files from sibling subfolders. The user clicks Import and gets a
 * Frankenstein. The footer breakdown + "Keep only here" button use this
 * helper to give the user a quick path back to a single-project import.
 */
export function partitionSelectedByScope(selectedRecords: any, { pathPrefix = '' } = {}) {
    const arr = Array.isArray(selectedRecords) ? selectedRecords : [];
    if (!pathPrefix) {
        const all = arr.filter(Boolean);
        return { inScope: all, outOfScope: [], inScopeCount: all.length, outOfScopeCount: 0 };
    }
    const inScope = [];
    const outOfScope = [];
    for (const r of arr) {
        if (!r) continue;
        const rel = r.relativePath || r.webkitRelativePath || r.name || '';
        if (rel.startsWith(pathPrefix)) inScope.push(r);
        else outOfScope.push(r);
    }
    return { inScope, outOfScope, inScopeCount: inScope.length, outOfScopeCount: outOfScope.length };
}

function throwIfMaterializationAborted(signal: AbortSignal | null | undefined) {
    if (!signal?.aborted) return;
    const error = new Error('Queued file materialisation was stopped.');
    error.name = 'AbortError';
    throw error;
}

export async function materializeExplorerFiles(
    fileRecords: any,
    { projectName = '', abortSignal = null }: { projectName?: string; abortSignal?: AbortSignal | null } = {},
) {
    const source = Array.isArray(fileRecords) ? fileRecords : [];
    const files = [];
    const warnings = [];
    throwIfMaterializationAborted(abortSignal);
    for (const record of source) {
        throwIfMaterializationAborted(abortSignal);
        if (!record || record.kind === 'unsupported') continue;
        if (record.readable === false) {
            warnings.push({ path: record.relativePath || record.name || '', message: record.readError || 'File was not readable during scan.' });
            continue;
        }
        if (!record.handle || typeof record.handle.getFile !== 'function') continue;
        try {
            throwIfMaterializationAborted(abortSignal);
            const file = await record.handle.getFile();
            throwIfMaterializationAborted(abortSignal);
            files.push(attachRelativePath(file, record.relativePath || file.name, projectName, record));
        } catch (err) {
            if ((err as any)?.name === 'AbortError') throw err;
            throwIfMaterializationAborted(abortSignal);
            warnings.push({ path: record.relativePath || record.name || '', message: errorMessage(err) });
        }
    }
    try {
        Object.defineProperty(files, 'materializeWarnings', {
            value: warnings,
            configurable: true,
            enumerable: false,
        });
    } catch {
        (files as any).materializeWarnings = warnings;
    }
    return files;
}

export function formatFileSize(bytes: any) {
    const n = Number(bytes || 0);
    if (!Number.isFinite(n) || n <= 0) return '0 KB';
    if (n >= 1024 * 1024 * 1024) return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
    if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${Math.ceil(n / 1024)} KB`;
}
